import React, { useEffect, useState, useMemo, useCallback } from 'react'
import BigNumber from 'bignumber.js'
import {
  useAccount,
  useContractWrite,
  useContractRead,
  usePrepareContractWrite,
  useWaitForTransaction,
  useBalance,
} from 'wagmi'
import { parseUnits, parseEther, formatEther, formatUnits } from 'viem'
import { contractAddr1, tokenAdd, chainId } from '../../config'
import erc20Abi from '../../abi/erc20.json'
import stakeAbi from '../../abi/stake.json'
import { toast } from 'react-hot-toast'
import ConnectWallet from './../ConnectWallet'
import './stake.css'

const StakingCard1 = ({ buttonColor = '#00f6ff' }) => {
  const { address } = useAccount()
  const [amount, setAmount] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [allowance, setAllowance] = useState(new BigNumber(0))
  const [shouldStakeAfterApprove, setShouldStakeAfterApprove] = useState(false)

  const totalStaked = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'totalStaked',
    enabled: true,
    watch: true,
    chainId: chainId,
  })

  const getRate = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'rewardBasisPoints',
    enabled: true,
    watch: true,
    chainId: chainId,
  })

  const getWhitelistEnabled = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'whitelistEnabled',
    enabled: true,
    watch: true,
    chainId: chainId,
  })

  const getWhitelistCheck = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'whitelist',
    args: [address],
    enabled: !!address,
    watch: true,
    chainId: chainId,
  })

  const getUserStakeInfo = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'getUserStakes',
    args: [address],
    enabled: !!address,
    watch: true,
    chainId: chainId,
  })

  const remaining = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'tokensLeftForStaking',
    enabled: true,
    watch: true,
    chainId: chainId,
  })

  const pendingRewards = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'pendingRewards',
    args: [address],
    enabled: !!address,
    watch: true,
    chainId: chainId,
  })

  const getPeriods = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'stakingPeriod',
    enabled: true,
    watch: true,
    chainId: chainId,
  })

  const maxStakeAmount = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'maxStakingAmount',
    enabled: true,
    watch: true,
    chainId: chainId,
  })

  const minAmount = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'minStakeAmount',
    enabled: true,
    watch: true,
    chainId: chainId,
  })

  const getRewardMode = useContractRead({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'distributionType',
    enabled: true,
    watch: true,
    chainId: chainId,
  })

  const tokenBalance = useContractRead({
    address: tokenAdd,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [address],
    enabled: !!address,
    watch: true,
    chainId: chainId,
  })

  const allowanceGet = useContractRead({
    address: tokenAdd,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [address, contractAddr1],
    enabled: !!address,
    watch: true,
    chainId,
  })

  useEffect(() => {
    if (allowanceGet.data !== undefined) {
      const allowanceValue = new BigNumber(allowanceGet.data)
      setAllowance(allowanceValue)
    }
  }, [address, allowanceGet.data])

  const formattedBalance = new BigNumber(tokenBalance.data || 0)
    .dividedBy(new BigNumber(10).pow(6))
    .toFixed(0)
  const remainingstake = new BigNumber(remaining.data || 0)
    .dividedBy(new BigNumber(10).pow(6))
    .toFixed(0)
  const totalstake = new BigNumber(totalStaked.data || 0)
    .dividedBy(new BigNumber(10).pow(6))
    .toFixed(0)
  const earnings = new BigNumber(pendingRewards.data || 0)
    .dividedBy(new BigNumber(10).pow(6))
    .toFixed(0)
  const maxStake = new BigNumber(maxStakeAmount.data || 0)
    .dividedBy(new BigNumber(10).pow(6))
    .toFixed(0)
  const minStakeAmount = new BigNumber(minAmount.data || 0)
    .dividedBy(new BigNumber(10).pow(6))
    .toFixed(0)

  const getStakeTimeInDays = new BigNumber(getPeriods.data || 0).dividedBy(60 * 60 * 24).toFixed(0)
  const getStakeTimeInHours = new BigNumber(getPeriods.data || 0).dividedBy(60 * 60).toFixed(0)

  const rateStakeBP = new BigNumber(getRate.data || 0)
  const rateStake = rateStakeBP.dividedBy(100).toFixed(2)

  const progressPercent = new BigNumber(totalstake).dividedBy(maxStake).multipliedBy(100).toFixed(0)
  const estimatedReward = ((parseFloat(amount) || 0) * rateStake) / 100

  const handleAmount = useMemo(
    () => (event) => {
      const inputValue = event.target.value
      const parsedAmount = Number(inputValue)
      const minAmountBN = new BigNumber(minStakeAmount)
      const balanceBN = new BigNumber(formattedBalance)

      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        setErrorMessage('Amount must be greater than zero.')
      } else if (balanceBN.isLessThan(parsedAmount)) {
        setErrorMessage('Insufficient balance.')
      } else if (new BigNumber(parsedAmount).isLessThan(minAmountBN)) {
        setErrorMessage(`Minimum stake amount is ${minStakeAmount} IP.`)
      } else {
        setErrorMessage('')
      }

      setAmount(inputValue)
    },
    [formattedBalance, minStakeAmount],
  )

  // Approve
  const { config: approveConfig } = usePrepareContractWrite({
    address: tokenAdd,
    abi: erc20Abi,
    functionName: 'approve',
    args: [contractAddr1, parseUnits(amount, 6)],
    enabled: !!address && !!amount,
    chainId,
  })

  const { write: approveWrite, data: approveData } = useContractWrite(approveConfig)

  const {
    isLoading: approvingLoading,
    isSuccess: approveSuccess,
    isError: approveError,
  } = useWaitForTransaction({
    hash: approveData?.hash,
  })

  // Stake
  const { config: stakeConfig } = usePrepareContractWrite({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'stake',
    args: [parseUnits(amount, 6)],
    enabled: !!address && !!amount,
    chainId,
  })

  const { write: stakeWrite, data: stakeData } = useContractWrite(stakeConfig)

  const {
    isLoading: stakeLoading,
    isSuccess: stakeSuccess,
    isError: stakeError,
  } = useWaitForTransaction({
    hash: stakeData?.hash,
  })

  useEffect(() => {
    if (stakeData?.hash) {
      setAllowance(new BigNumber(0))
    }
  }, [stakeData?.hash])

  // Claim
  const { config: claimConfig } = usePrepareContractWrite({
    address: contractAddr1,
    abi: stakeAbi,
    functionName: 'claimRewards',
    enabled: !!address,
    chainId,
  })

  const { write: claimWrite, data: claimData } = useContractWrite(claimConfig)

  const {
    isLoading: claimLoading,
    isSuccess: claimSuccess,
    isError: claimError,
  } = useWaitForTransaction({
    hash: claimData?.hash,
  })

  useEffect(() => {
    if (stakeSuccess) {
      toast.success(
        <div className="text-center py-2">
          Stake Success!
          <div>
            <a style={{ color: '#000' }} href={`https://polygonscan.com/tx/${stakeData?.hash}`}>
              View On Bscscan
            </a>
          </div>
        </div>,
      )
      const timeout = setTimeout(() => {
        toast.dismiss()
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [stakeSuccess, stakeData?.hash])

  useEffect(() => {
    if (stakeError) {
      toast.error(<div className="text-center py-2">Error! Something Went Wrong</div>)
      const timeout = setTimeout(() => {
        toast.dismiss()
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [stakeError])

  useEffect(() => {
    if (approveSuccess) {
      toast.success(
        <div className="text-center py-2">
          Approve Success!
          <div>
            <a style={{ color: '#000' }} href={`https://polygonscan.com/tx/${approveData?.hash}`}>
              View On Bscscan
            </a>
          </div>
        </div>,
      )
      const timeout = setTimeout(() => {
        toast.dismiss()
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [approveSuccess, approveData?.hash])

  useEffect(() => {
    if (approveError) {
      toast.error(<div className="text-center py-2">Error! Something Went Wrong</div>)
      const timeout = setTimeout(() => {
        toast.dismiss()
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [approveError])

  useEffect(() => {
    if (claimSuccess) {
      toast.success(
        <div className="text-center py-2">
          Claim Success!
          <div>
            <a style={{ color: '#000' }} href={`https://polygonscan.com/tx/${claimData?.hash}`}>
              View On Bscscan
            </a>
          </div>
        </div>,
      )
      const timeout = setTimeout(() => {
        toast.dismiss()
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [claimSuccess, claimData?.hash])

  useEffect(() => {
    if (claimError) {
      toast.error(<div className="text-center py-2">Error! Something Went Wrong</div>)
      const timeout = setTimeout(() => {
        toast.dismiss()
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [claimError])

  const isWhitelisted = getWhitelistCheck.data
  const isWhitelistLoading = getWhitelistCheck.isLoading
  const whitelistEnabled = getWhitelistEnabled.data

  const stakeFn = useCallback(() => {
    stakeWrite?.()
  }, [stakeWrite])

  const handleStakeFlow = () => {
    setShouldStakeAfterApprove(true)
    approveWrite?.()
  }

  useEffect(() => {
    if (approveSuccess && shouldStakeAfterApprove) {
      stakeFn()
      setShouldStakeAfterApprove(false)
    }
  }, [approveSuccess, shouldStakeAfterApprove, stakeFn])

  const formatNumberWithCommas = (number) => {
    return new BigNumber(number).toFormat()
  }

  return (
    <div className="staking-card-container">
      <div className="staking-card">
        {whitelistEnabled && <div className="ribbon">Whitelist Pool</div>}
        <div className="progress-wrapper">
          <svg className="progress-circle" viewBox="0 0 36 36">
            <path
              className="circle-bg"
              d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831a15.9155 15.9155 0 1 1 0 -31.831"
            />
            <path
              className="circle"
              strokeDasharray={`${progressPercent.toString()}, 100`}
              d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831a15.9155 15.9155 0 1 1 0 -31.831"
            />
            <text
              x="18"
              y="18"
              className="percentage-text"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {progressPercent.toString()}%
            </text>
          </svg>
        </div>

        <h4 className="total-stake">Total Max Mining Amount</h4>
        <h3 className="amount">{formatNumberWithCommas(maxStake)} IP</h3>

        <div className="profit-return">Profit Return: {rateStake.toString()}%</div>
        <div className="wallet-balance">
          Wallet Balance: {formatNumberWithCommas(formattedBalance)} IP
        </div>
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-label">Period</div>
            <div className="stat-value">{getStakeTimeInDays.toString()} days</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Total Mining</div>
            <div className="stat-value">{formatNumberWithCommas(totalstake)}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Remaining</div>
            <div className="stat-value">{formatNumberWithCommas(remainingstake)}</div>
          </div>
        </div>

        <div className="stake-input-container">
          <input
            type="number"
            placeholder="Enter mining amount"
            className="stake-input"
            value={amount}
            onChange={handleAmount}
          />
          <button className="max-button" onClick={() => setAmount(formattedBalance)}>
            MAX
          </button>
        </div>

        {errorMessage && (
          <div>
            <p style={{ color: '#ff0000' }}>{errorMessage}</p>
          </div>
        )}

        {/*

        {!address ? (
          <div className="text-center mb-2">
            <ConnectWallet />
          </div>
        ) : whitelistEnabled && !isWhitelistLoading && !isWhitelisted ? (
          <p style={{ color: 'red' }}>You are not whitelisted to mine.</p>
        ) : allowance.isLessThan(amount) ? (
          <button
            className="connect-button"
            onClick={() => approveWrite?.()}
            disabled={!approveWrite || approvingLoading || !!errorMessage}
            style={{ backgroundColor: buttonColor }}
          >
            {approvingLoading ? 'Approving...' : 'Approve'}
          </button>
        ) : (
          <button
            className="connect-button"
            onClick={() => stakeWrite?.()}
            disabled={!stakeWrite || stakeLoading}
            style={{ backgroundColor: buttonColor }}
          >
            {stakeLoading ? 'Staking...' : 'Stake Now'}
          </button>
        )}

        */}

        {!address ? (
          <div className="text-center mb-2">
            <ConnectWallet />
          </div>
        ) : whitelistEnabled && !isWhitelistLoading && !isWhitelisted ? (
          <p style={{ color: 'red' }}>You are not whitelisted to mine.</p>
        ) : (
          <button
            className="connect-button"
            onClick={handleStakeFlow}
            disabled={!address || !amount || !!errorMessage || approvingLoading || stakeLoading}
            style={{ backgroundColor: buttonColor }}
          >
            {approvingLoading || (shouldStakeAfterApprove && !approveSuccess)
              ? 'Approving...'
              : stakeLoading
              ? 'Mining...'
              : 'Approve & Mining'}
          </button>
        )}

        <div className="staking-info mt-4">
          You are mining {formatNumberWithCommas(amount || 0)} IP for{' '}
          {formatNumberWithCommas(getStakeTimeInDays)} days and will earn{' '}
          {formatNumberWithCommas(estimatedReward)} USDT ({rateStake.toString()}% return).
        </div>
      </div>
    </div>
  )
}

export default StakingCard1
